<template>
  <div v-if="qrVisible" id="scanner-container">
    <button class="btn btn-lg mute-button" type="button" @click="toggleMute">
      <font-awesome-icon
        size="xl"
        :icon="isMuted ? 'volume-mute' : 'volume-up'"
      />
    </button>
    <div
      v-if="!cameraPermissionRequested"
      class="h-100 d-flex flex-column justify-content-center align-items-center"
    >
      <div class="alert alert-danger" role="alert">
        <font-awesome-icon icon="exclamation-triangle" />
        Camera Access Required: Please enable camera access to scan QR codes.
        Disabling it will prevent QR code scanning functionality.
      </div>
      <button
        class="btn btn-lg btn-primary"
        type="button"
        @click="cameraPermissionRequested = true"
      >
        <font-awesome-icon icon="camera" />
        Click here to grant camera permission
      </button>
    </div>
    <div v-if="userDeniedAccess" class="h-100 d-flex align-items-center mx-1">
      <div class="flex-grow-1 alert alert-danger" role="alert">
        Camera permission must be enabled to scan QR codes.
      </div>
    </div>
    <div v-else class="scanner flex-grow-1">
      <qrcode-stream
        v-if="cameraPermissionRequested"
        :constraints="{ facingMode }"
        @camera-on="onCameraOn"
        @camera-off="onCameraOff"
        @detect="onDetect"
        @error="onError"
      />
    </div>
    <div class="panel py-2">
      <button
        class="btn btn-lg btn-outline-danger"
        type="button"
        @click="hideQR"
      >
        <font-awesome-icon icon="times" />
        Cancel
      </button>
    </div>
  </div>
</template>
<script>
import { useRouter } from "vue-router";
import { ref, onBeforeUnmount } from "vue";
import { QrcodeStream } from "vue-qrcode-reader";
import { Howl } from "howler";
import { Hub } from "aws-amplify/utils";

export default {
  name: "QRScanner",
  components: {
    QrcodeStream,
  },
  setup() {
    const qrVisible = ref(false);
    const cameraPermissionRequested = ref(false);
    const userDeniedAccess = ref(false);
    const noRearCamera = ref(false);
    const noFrontCamera = ref(false);
    const facingMode = ref("environment");
    const isMuted = ref(false);
    const showToastError = (message) => {
      Hub.dispatch("toast", {
        event: "error",
        message,
      });
    };
    const showToastSuccess = (message) => {
      Hub.dispatch("toast", {
        event: "success",
        message,
        data: {
          timeout: 3000,
        },
      });
    };
    const router = useRouter();

    onBeforeUnmount(() => {
      noRearCamera.value = false;
      noFrontCamera.value = false;
      facingMode.value = "environment";
    });

    const toggleMute = () => {
      isMuted.value = !isMuted.value;
    };

    const loadAudioFiles = () => {
      if (!window.howlerSoundGood) {
        window.howlerSoundGood = new Howl({
          src: ["/sounds/confirmation_001.mp3"],
        });
      }

      if (!window.howlerSoundBad) {
        window.howlerSoundBad = new Howl({
          src: ["/sounds/error_007.mp3"],
        });
      }
    };

    const onCameraOff = () => {};

    const onCameraOn = () => {
      if (noRearCamera.value && noFrontCamera.value) {
        showToastError("No camera found");
      }

      if (noRearCamera.value) {
        facingMode.value = "user";
      }

      if (noFrontCamera.value) {
        facingMode.value = "environment";
      }
    };

    const checkCameraPermissionState = async () => {
      try {
        // querying camera permission state currently only works in Chrome
        const result = await navigator.permissions.query({ name: "camera" });
        cameraPermissionRequested.value =
          result.state === "granted" || result.state === "denied";

        if (result.state === "denied") {
          console.warn("Camera permission denied", result);
        }
      } catch (error) {
        // fallback to loading the QR scanner if querying camera permissions fails
        cameraPermissionRequested.value = true;
        console.error("Failed to query camera permissions:", error);
      }
    };

    const onDetect = (decodedContent) => {
      const coactiveRegex =
        /coactivecare\.co\.uk\/clients\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\?from=qr$/;
      const [firstUrl] = decodedContent;

      if (coactiveRegex.test(firstUrl.rawValue)) {
        showToastSuccess("QR code detected");
        if (!isMuted.value) {
          window.howlerSoundGood.play();
        }

        const [, clientId] = coactiveRegex.exec(firstUrl.rawValue);
        Hub.dispatch("qr", {
          event: "hide",
        });
        router.push({
          path: `/clients/${clientId}`,
          query: {
            from: "qr",
          },
        });
      } else {
        if (!isMuted.value) {
          window.howlerSoundBad.play();
        }
        console.error("Invalid QR code", decodedContent);
        showToastError("Invalid QR code");
      }
    };

    const onError = (error) => {
      if (error.name === "NotAllowedError") {
        userDeniedAccess.value = true;
        showToastError("Please allow camera permission");
        return;
      }

      showToastError("QR code error " + error.name);

      const triedFrontCamera = facingMode.value === "front";
      const triedRearCamera = facingMode.value === "rear";
      const cameraMissingError = error.name === "OverconstrainedError";

      if (triedRearCamera && cameraMissingError) {
        noRearCamera.value = true;
      }

      if (triedFrontCamera && cameraMissingError) {
        noFrontCamera.value = true;
      }
    };

    const hideQR = () => {
      Hub.dispatch("qr", {
        event: "hide",
      });
    };

    Hub.listen("qr", async ({ payload }) => {
      switch (payload.event) {
        case "show":
          userDeniedAccess.value = false;
          qrVisible.value = true;
          // hide page scrollbars
          document.body.style.overflow = "hidden";
          await checkCameraPermissionState();
          loadAudioFiles();
          break;
        case "hide":
          qrVisible.value = false;
          // show page scrollbars
          document.body.style.overflow = "initial";
          break;
        default:
      }
    });

    return {
      onDetect,
      QrcodeStream,
      onError,
      onCameraOn,
      onCameraOff,
      qrVisible,
      noRearCamera,
      noFrontCamera,
      facingMode,
      hideQR,
      isMuted,
      toggleMute,
      userDeniedAccess,
      cameraPermissionRequested,
    };
  },
};
</script>

<style lang="scss">
#scanner-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-direction: column;
  overflow: hidden;
  z-index: 1040; // make sure it's above the navbar
  background-color: rgba(255, 255, 255, 0.9);

  > * {
    min-height: 0;
  }

  video {
    height: 100%;
  }

  .qrcode-stream-wrapper {
    width: 100%;
    height: 100%;
    text-align: center;
  }

  .qrcode-stream-overlay {
    display: none;
  }

  .panel {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    background: rgba(0, 0, 0, 0.1);
  }

  .mute-button {
    position: fixed;
    left: 10px;
    top: 10px;
    padding: 10px;
    background: rgba(0, 0, 0, 0.1);
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    z-index: 1041; // make it clickable
  }
}
</style>
